import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Sparkles, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CAD", "AUD"];

const InvoiceBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tier = (user?.subscription_tier || "free").toLowerCase();
  const isPro = tier === "pro" || tier === "team" || tier === "studio";

  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    currency: "USD",
    tax_percent: 0,
    notes: "",
    due_date: "",
  });
  const [items, setItems] = useState([{ _key: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const tax = subtotal * (Number(form.tax_percent || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [items, form.tax_percent]);

  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((p) => [...p, { _key: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const generateWithAI = async () => {
    if (!isPro) {
      navigate("/pricing");
      return;
    }
    const prompt = aiPrompt.trim();
    if (prompt.length < 5) {
      toast.error("Describe the work in a sentence or two.");
      return;
    }
    setAiLoading(true);
    try {
      const r = await api.post("/ai/invoice-draft", { prompt });
      const d = r.data?.draft;
      if (!d) throw new Error("Empty draft");
      setForm((f) => ({
        ...f,
        client_name: d.client_name || f.client_name,
        client_email: d.client_email || f.client_email,
        currency: d.currency || f.currency,
        tax_percent: d.tax_percent ?? f.tax_percent,
        due_date: d.due_date || f.due_date,
        notes: d.notes || f.notes,
      }));
      if (Array.isArray(d.items) && d.items.length) {
        setItems(
          d.items.map((it) => ({
            _key: crypto.randomUUID(),
            description: it.description || "",
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
          }))
        );
      }
      toast.success("Draft generated — review before sending.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(detail || "Upgrade to Pro to use the AI Assistant.");
        navigate("/pricing");
      } else {
        toast.error(detail || "AI draft failed. Try rephrasing.");
      }
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async (sendNow) => {
    if (!form.client_name || !form.client_email) {
      toast.error("Client name & email required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/invoices", {
        ...form,
        tax_percent: Number(form.tax_percent || 0),
        items: items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity || 0),
          unit_price: Number(i.unit_price || 0),
        })),
      });
      if (sendNow) {
        await api.post(`/invoices/${r.data.invoice_id}/send`, { origin_url: window.location.origin });
        toast.success("Invoice created & sent");
      } else {
        toast.success("Invoice saved as draft");
      }
      navigate("/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const Input = ({ label, ...rest }) => (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">{label}</div>
      <input
        {...rest}
        className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white"
      />
    </label>
  );

  return (
    <div data-testid="invoice-builder-page">
      <PageHeader
        overline="Billing"
        title="New invoice"
        description="Bill any client in any currency. Accept on-chain crypto payments."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => submit(false)}
              disabled={saving}
              data-testid="save-draft-btn"
              className="px-4 py-2 rounded-md text-sm border border-[#2c241e] hover:border-zinc-500 disabled:opacity-50"
            >Save draft</button>
            <button
              onClick={() => submit(true)}
              disabled={saving}
              data-testid="send-now-btn"
              className="bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 disabled:opacity-50"
            >Send invoice</button>
          </div>
        }
      />

      <div className="p-4 md:p-8 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-8 space-y-8">
          {/* AI Invoice Assistant */}
          <section
            data-testid="ai-assistant-section"
            className="relative border border-[#2c241e] p-6 bg-gradient-to-br from-[#0E0E10] via-[#0A0A0A] to-[#0A0A0A] overflow-hidden"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl"
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-amber-300" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                    AI Invoice Assistant
                  </div>
                  {!isPro && (
                    <span
                      data-testid="ai-pro-badge"
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-amber-300/90 border border-amber-300/30 px-1.5 py-0.5"
                    >
                      <Lock size={9} /> Pro
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-zinc-500 hidden md:block">
                  Powered by Claude Sonnet 4.5
                </span>
              </div>

              <p className="text-sm text-zinc-400 mb-4 max-w-2xl">
                Describe the work in plain English — Quill drafts the invoice for you.
              </p>

              <div className="grid grid-cols-12 gap-3">
                <textarea
                  data-testid="ai-prompt-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={!isPro || aiLoading}
                  placeholder={
                    isPro
                      ? 'e.g. "Invoice Acme Corp $2,500 for July retainer plus 4 hrs of editing at $120/hr, due August 15."'
                      : "Unlock with Pro to draft invoices from a single sentence."
                  }
                  rows={3}
                  className="col-span-12 md:col-span-9 bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-amber-300/60 disabled:opacity-60 disabled:cursor-not-allowed resize-none"
                />
                <button
                  data-testid={isPro ? "ai-generate-btn" : "ai-upgrade-btn"}
                  onClick={generateWithAI}
                  disabled={aiLoading}
                  className={
                    "col-span-12 md:col-span-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 " +
                    (isPro
                      ? "bg-amber-300 text-black hover:bg-amber-200"
                      : "bg-transparent text-amber-200 border border-amber-300/40 hover:border-amber-300")
                  }
                >
                  {aiLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Drafting…
                    </>
                  ) : isPro ? (
                    <>
                      <Sparkles size={14} /> Generate draft
                    </>
                  ) : (
                    <>
                      <Lock size={14} /> Upgrade to Pro
                    </>
                  )}
                </button>
              </div>

              {!isPro && (
                <div className="mt-3 text-[11px] text-zinc-500">
                  Pro unlocks unlimited AI drafts, branded invoices, and custom themes.
                </div>
              )}
            </div>
          </section>

          {/* Client */}
          <section className="border border-[#2c241e] p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Client</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Name"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                data-testid="client-name-input"
                placeholder="Acme Co."
              />
              <Input
                label="Email"
                type="email"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                data-testid="client-email-input"
                placeholder="billing@acme.com"
              />
            </div>
          </section>

          {/* Items */}
          <section className="border border-[#2c241e] p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Line items</div>
              <button
                onClick={addItem}
                data-testid="add-item-btn"
                className="text-sm inline-flex items-center gap-1 text-zinc-300 hover:text-white py-2 px-2 -mr-2 -my-2 rounded-md hover:bg-[#241c17]"
              ><Plus size={14} /> Add item</button>
            </div>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={it._key} className="grid grid-cols-12 gap-2 sm:gap-3 items-center" data-testid={`item-row-${i}`}>
                  <input
                    placeholder="Description"
                    value={it.description}
                    onChange={(e) => setItem(i, "description", e.target.value)}
                    data-testid={`item-desc-${i}`}
                    className="col-span-12 sm:col-span-6 min-w-0 bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Qty"
                    value={it.quantity}
                    onChange={(e) => setItem(i, "quantity", e.target.value)}
                    data-testid={`item-qty-${i}`}
                    className="col-span-4 sm:col-span-2 min-w-0 bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    value={it.unit_price}
                    onChange={(e) => setItem(i, "unit_price", e.target.value)}
                    data-testid={`item-price-${i}`}
                    className="col-span-6 sm:col-span-3 min-w-0 bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white"
                  />
                  <button
                    onClick={() => removeItem(i)}
                    data-testid={`item-remove-${i}`}
                    aria-label="Remove line item"
                    className="col-span-2 sm:col-span-1 text-zinc-500 hover:text-rose-400 flex justify-center items-center h-10 rounded-md hover:bg-rose-500/10"
                  ><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Notes */}
          <section className="border border-[#2c241e] p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Notes (optional)</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="notes-input"
              placeholder="Thank you for your business."
              rows={3}
              className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
            />
          </section>
        </div>

        {/* Sidebar */}
        <aside className="md:col-span-4 space-y-6">
          <section className="border border-[#2c241e] p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Settings</div>
            <div className="space-y-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Currency</div>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  data-testid="currency-select"
                  className="w-full bg-[#0A0A0A] border border-[#2c241e] px-3 py-2.5 text-sm focus:outline-none focus:border-white"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <Input
                label="Tax %"
                type="number"
                step="0.1"
                value={form.tax_percent}
                onChange={(e) => setForm({ ...form, tax_percent: e.target.value })}
                data-testid="tax-input"
              />
              <Input
                label="Due date"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="due-date-input"
              />
            </div>
          </section>

          <section className="border border-[#2c241e] p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Summary</div>
            <div className="space-y-3 font-mono">
              <div className="flex justify-between text-sm text-zinc-400">
                <span>Subtotal</span>
                <span>{fmtMoney(totals.subtotal, form.currency)}</span>
              </div>
              <div className="flex justify-between text-sm text-zinc-400">
                <span>Tax</span>
                <span>{fmtMoney(totals.tax, form.currency)}</span>
              </div>
              <div className="border-t border-[#2c241e] pt-3 flex justify-between text-base text-white" data-testid="invoice-total">
                <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-zinc-500 self-center">Total</span>
                <span>{fmtMoney(totals.total, form.currency)}</span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default InvoiceBuilder;
